import type { AgentCardProps } from '../types'

// Tip renkleri
const typeColors: Record<string, string> = {
  developer: '#10b981',
  researcher: '#3b82f6',
  designer: '#f59e0b',
  analyst: '#8b5cf6',
}

// Durum renkleri
const statusColors: Record<string, string> = {
  available: '#10b981',
  busy: '#f59e0b',
  offline: 'var(--secondary)',
}

// Durum etiketleri
const statusLabels: Record<string, string> = {
  available: 'Musait',
  busy: 'Mesgul',
  offline: 'Cevrimdisi',
}

// Tip etiketleri
const typeLabels: Record<string, string> = {
  developer: 'Gelistirici',
  researcher: 'Arastirmaci',
  designer: 'Tasarimci',
  analyst: 'Analist',
}

export default function AgentCard({ agent, onClick }: AgentCardProps) {
  const typeColor = typeColors[agent.type ?? 'general'] || 'var(--secondary)'
  const statusColor = statusColors[agent.status] || 'var(--secondary)'

  return (
    <article
      onClick={() => onClick?.(agent.id)}
      style={{
        backgroundColor: 'var(--bg-secondary)',
        border: '1px solid var(--border-color)',
        borderRadius: '12px',
        padding: '20px',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        display: 'flex',
        flexDirection: 'column',
        gap: '15px',
        height: '100%',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-4px)'
        e.currentTarget.style.boxShadow = '0 8px 16px rgba(0, 0, 0, 0.1)'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)'
        e.currentTarget.style.boxShadow = 'none'
      }}
    >
      {/* Header - Type & Status */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <span
            style={{
              padding: '4px 12px',
              backgroundColor: `${typeColor  }20`,
              color: typeColor,
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: '500',
            }}
          >
            {typeLabels[agent.type ?? 'general']}
          </span>

          {/* Visibility Badge */}
          {(agent as any).visibility === 'marketplace' && (
            <span
              style={{
                padding: '4px 8px',
                backgroundColor: '#10b98120',
                color: '#10b981',
                borderRadius: '6px',
                fontSize: '11px',
                fontWeight: '500',
              }}
            >
              Marketplace
            </span>
          )}
          {(agent as any).visibility === 'private' && (
            <span
              style={{
                padding: '4px 8px',
                backgroundColor: '#6b728020',
                color: 'var(--secondary)',
                borderRadius: '6px',
                fontSize: '11px',
                fontWeight: '500',
              }}
            >
              Private
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: statusColor,
            }}
          />
          <span
            style={{
              fontSize: '11px',
              fontWeight: '500',
              color: statusColor,
            }}
          >
            {statusLabels[agent.status]}
          </span>
        </div>
      </div>

      {/* Name & Level */}
      <div>
        <h3
          style={{
            fontSize: '18px',
            fontWeight: '600',
            color: 'var(--text-primary)',
            margin: 0,
            marginBottom: '4px',
          }}
        >
          {agent.name}
        </h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '13px', color: 'var(--text-tertiary)' }}>
            Level {agent.level}
          </span>
          <span style={{ color: 'var(--border-color)' }}>•</span>
          <span style={{ fontSize: '13px', color: typeColor, fontWeight: '500' }}>
            ${agent.hourly_rate}/saat
          </span>
        </div>
      </div>

      {/* Description */}
      <p
        style={{
          fontSize: '14px',
          color: 'var(--text-secondary)',
          margin: 0,
          lineHeight: '1.6',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          flex: 1,
        }}
      >
        {agent.description}
      </p>

      {/* Performance Score */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
          <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>Performans</span>
          <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '500' }}>
            {agent.performance_score}/100
          </span>
        </div>
        <div
          style={{
            width: '100%',
            height: '6px',
            backgroundColor: 'var(--bg-tertiary)',
            borderRadius: '3px',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${agent.performance_score}%`,
              height: '100%',
              backgroundColor: typeColor,
              transition: 'width 0.3s ease',
            }}
          />
        </div>
      </div>

      {/* Stats */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '10px',
          paddingTop: '15px',
          borderTop: '1px solid var(--border-color)',
        }}
      >
        {/* Tasks Completed */}
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>
            {agent.tasks_completed}
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '2px' }}>
            Gorev
          </div>
        </div>

        {/* Success Rate */}
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '16px', fontWeight: '600', color: typeColor }}>
            {agent.success_rate}%
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '2px' }}>
            Basari
          </div>
        </div>

        {/* Level */}
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>
            Lv.{agent.level}
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '2px' }}>
            Seviye
          </div>
        </div>
      </div>

      {/* Capabilities */}
      {agent.capabilities && agent.capabilities.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {agent.capabilities.slice(0, 3).map((capability, index) => (
            <span
              key={index}
              style={{
                padding: '3px 8px',
                backgroundColor: 'var(--bg-tertiary)',
                color: 'var(--text-tertiary)',
                borderRadius: '4px',
                fontSize: '11px',
              }}
            >
              {capability}
            </span>
          ))}
          {agent.capabilities.length > 3 && (
            <span
              style={{
                padding: '3px 8px',
                color: 'var(--text-tertiary)',
                fontSize: '11px',
              }}
            >
              +{agent.capabilities.length - 3}
            </span>
          )}
        </div>
      )}

      {/* Owner */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingTop: '10px' }}>
        <div
          style={{
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            backgroundColor: `${typeColor  }30`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '12px',
            fontWeight: '600',
            color: typeColor,
          }}
        >
          {agent.owner?.display_name.charAt(0).toUpperCase()}
        </div>
        <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
          {agent.owner?.display_name}
        </span>
      </div>
    </article>
  )
}
